/**
 * Central query-key factory. One source of truth for every cache key so that
 * `useQuery` (reads) and `invalidateQueries` (writes) can never disagree.
 *
 * Key design: hierarchical arrays. Invalidating a prefix invalidates all keys
 * under it — e.g. invalidating `qk.tasks.all` (`['tasks']`) also invalidates
 * every `['tasks','list',...]`, `['tasks','detail',id]`, and `['tasks',id,...]`.
 */
export const qk = {
  // `/users/me` (blueprints/users) — flat profile fields (username, avatar,
  // plan…), read/written by the Settings profile tab. Do NOT repoint this at
  // the identity domain's `/api/me` — see `identity()` below.
  me: () => ['me'] as const,

  // `/api/me` (domains/identity) — org + role + resolved preferences + the
  // orgs list. Deliberately a SEPARATE key from `me()` above: the plan that
  // introduced preferences (023) assumed these were the same cache entry —
  // they are two different endpoints that happen to share the word "me".
  // Repointing `me()` at this shape would have silently broken the profile
  // tab's `useApi(apiClient.getMe(), {queryKey: qk.me()})` read.
  identity: () => ['identity'] as const,
  preferences: () => ['preferences'] as const,

  org: {
    current: () => ['org', 'current'] as const,
    members: () => ['org', 'members'] as const,
    // `filters` is the object passed to orgApi.listMembers — React Query hashes
    // it structurally, so equal filter sets share a cache entry. Kept separate
    // from `members()` so the unfiltered picker cache (AssigneePicker et al.)
    // isn't invalidated/refetched every time the directory's filters change.
    directory: (filters?: Record<string, unknown>) => ['org', 'directory', filters ?? {}] as const,
    intakeQuestions: () => ['org', 'intake-questions'] as const,
    roles: () => ['org', 'roles'] as const,
  },

  projects: {
    all: () => ['projects'] as const,
    list: (status?: 'active' | 'archived') => ['projects', 'list', status ?? 'active'] as const,
    // Keys on `public_id` (the route-param value) — everything reachable by
    // URL must agree on one id or two components cache the same row under
    // different keys and neither sees the other's invalidation (ADR 0007).
    detail: (id: string) => ['projects', 'detail', id] as const,
    members: (id: number) => ['projects', id, 'members'] as const,
    teams: (id: number) => ['projects', id, 'teams'] as const,
    statuses: (id: number) => ['projects', id, 'statuses'] as const,
    phases: (id: number) => ['projects', id, 'phases'] as const,
    activity: (id: number) => ['projects', id, 'activity'] as const,
    workload: (id: number) => ['projects', id, 'workload'] as const,
    statusReport: (id: number) => ['projects', id, 'status-report'] as const,
    dependencies: (id: number) => ['projects', id, 'dependencies'] as const,
    analytics: (id: number) => ['projects', id, 'analytics'] as const,
  },

  tasks: {
    all: () => ['tasks'] as const,
    // `filters` is the object passed to apiClient.listWorkTasks — React Query
    // hashes it structurally, so two calls with equal filters share a cache entry.
    //
    // INVARIANT: entries under this key hold the **unwrapped `Task[]`**, never
    // the `{items}` envelope. Two readers once disagreed (useTasks stored the
    // array, DailyPlannerTab stored the envelope) and whichever mounted last
    // poisoned the shared entry, crashing the other with "src is not iterable".
    // Read through `useTasks()` rather than hand-rolling `useApi` on this key.
    list: (filters?: Record<string, unknown>) =>
      ['tasks', 'list', filters ?? {}] as const,
    // Keyed on `public_id` — see the note on projects.detail above.
    detail: (id: string) => ['tasks', 'detail', id] as const,
    activity: (id: number) => ['tasks', id, 'activity'] as const,
    comments: (id: number) => ['tasks', id, 'comments'] as const,
    subscribers: (id: number) => ['tasks', id, 'subscribers'] as const,
    reminders: (id: number) => ['tasks', id, 'reminders'] as const,
    dependencies: (id: number) => ['tasks', id, 'dependencies'] as const,
    checklist: (id: number) => ['tasks', id, 'checklist'] as const,
    recurrence: (id: number) => ['tasks', id, 'recurrence'] as const,
    attachments: (id: number) => ['tasks', id, 'attachments'] as const,
    overdue: () => ['tasks', 'overdue'] as const,
  },

  approvals: () => ['approvals'] as const,
  portfolio: () => ['portfolio'] as const,
  reminders: () => ['reminders', 'all'] as const,
  labels: () => ['labels'] as const,
  recurring: () => ['recurring'] as const,
  dailyPlan: (date?: string) => ['daily-plan', date ?? 'today'] as const,

  templates: {
    task: () => ['templates', 'task'] as const,
    project: () => ['templates', 'project'] as const,
  },

  dashboard: {
    metrics: () => ['dashboard', 'metrics'] as const,
    teamActivity: () => ['dashboard', 'team-activity'] as const,
    performance: () => ['dashboard', 'performance'] as const,
  },

  insights: {
    teamActivity: () => ['insights', 'team-activity'] as const,
    performance: () => ['insights', 'performance'] as const,
    taskStats: () => ['insights', 'task-stats'] as const,
    personal: (days: number) => ['insights', 'personal', days] as const,
    team: (teamId: number) => ['insights', 'team', teamId] as const,
  },

  teams: {
    all: () => ['teams'] as const,
    // Org-wide picker cache — People.tsx, ProjectTeamsCard, InviteModal,
    // Requests.tsx share this one entry (GET /api/work/teams).
    orgList: () => ['teams', 'org-list'] as const,
    // The Departments page (plan 019) — server-side search + include_archived.
    // `filters` hashes structurally, same convention as org.directory.
    orgDirectory: (filters?: Record<string, unknown>) => ['teams', 'org-directory', filters ?? {}] as const,
    orgMembers: (id: number) => ['teams', 'org-members', id] as const,
  },

  schedules: {
    all: () => ['schedules'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['schedules', 'list', filters ?? {}] as const,
    detail: (id: number) => ['schedules', 'detail', id] as const,
  },

  hr: {
    all: () => ['hr'] as const,
    myLeave: (status?: string) => ['hr', 'leave', 'mine', status ?? 'all'] as const,
    balances: (year?: number, asOf?: string) => ['hr', 'leave', 'balances', year ?? 'current', asOf ?? 'today'] as const,
    employeeBalances: (userId: number, year?: number, asOf?: string) =>
      ['hr', 'leave', 'employee-balances', userId, year ?? 'current', asOf ?? 'today'] as const,
    eligibility: (startDate: string, endDate: string) => ['hr', 'leave', 'eligibility', startDate, endDate] as const,
    policies: (employmentType?: string, includePast?: boolean) =>
      ['hr', 'leave', 'policies', employmentType ?? 'all', includePast ? 'past' : 'current'] as const,
    calendar: (from: string, to: string, teamId?: number, leaveType?: string) =>
      ['hr', 'leave', 'calendar', from, to, teamId ?? 'all', leaveType ?? 'all'] as const,
    onLeaveCount: (on: string) => ['hr', 'leave', 'on-leave-count', on] as const,
    teamLeave: (teamId: number, status?: string) =>
      ['hr', 'leave', 'team', teamId, status ?? 'all'] as const,
    leaveApprovals: () => ['hr', 'leave', 'approvals'] as const,
    employeeDocuments: (userId: number) => ['hr', 'employee-documents', userId] as const,
  },

  finance: {
    all: () => ['finance'] as const,
    myExpenses: (status?: string) =>
      ['finance', 'expenses', 'mine', status ?? 'all'] as const,
    teamExpenses: (teamId: number, status?: string) =>
      ['finance', 'expenses', 'team', teamId, status ?? 'all'] as const,
    expenseApprovals: (params?: Record<string, unknown>) =>
      ['finance', 'expenses', 'approvals', params ?? null] as const,
    // `filters` hashes structurally (same convention as org.directory) so
    // equal filter sets share a cache entry.
    budgets: (filters?: Record<string, unknown>) => ['finance', 'budgets', filters ?? {}] as const,
    requisitionBudgets: (filters?: Record<string, unknown>) =>
      ['finance', 'requisition-budgets', filters ?? {}] as const,
    budget: (id: number) => ['finance', 'budgets', id] as const,
    projectPlan: (id: number) => ['finance', 'budgets', id, 'project-plan'] as const,
    expenseBudgetOptions: (projectId: number, expenseDate: string) =>
      ['finance', 'expense-budget-options', projectId, expenseDate] as const,
    projectBudgetReport: (filters: Record<string, unknown> | import('@/types').ProjectBudgetReportFilters) =>
      ['finance', 'project-budget-report', filters] as const,
    budgetDisbursements: (id: number) => ['finance', 'budgets', id, 'disbursements'] as const,
    overview: (filters?: Record<string, unknown>) => ['finance', 'overview', filters ?? {}] as const,
    disbursements: (filters?: Record<string, unknown>) => ['finance', 'disbursements', filters ?? {}] as const,
    myRequisitions: (filters?: Record<string, unknown>) => ['finance', 'requisitions', 'mine', filters ?? {}] as const,
    requisitionApprovals: (filters?: Record<string, unknown>) =>
      ['finance', 'requisitions', 'approvals', filters ?? {}] as const,
    receipts: (expenseId: number) => ['finance', 'expenses', expenseId, 'receipts'] as const,
  },

  invitation: (token: string) => ['invitation', token] as const,

  notifications: () => ['notifications'] as const,

  requests: {
    all: () => ['requests'] as const,
    history: (filters?: Record<string, unknown>) => ['requests', 'history', filters ?? {}] as const,
  },

  operations: {
    all: () => ['operations'] as const,
    activity: (filters?: Record<string, unknown>) => ['operations', 'activity', filters ?? {}] as const,
  },
} as const
